/**
 * ShadeScreen Configurator - Shopify Widget Entry Point
 * 
 * This file creates a self-contained widget that can be embedded on Shopify product pages.
 * It automatically finds and replaces ACO options while maintaining compatibility with the 
 * underlying ACO fields for cart/checkout functionality.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import ShopifyConfigurator from './ShopifyConfigurator';
import './shopify-configurator.css';

// Configuration object that merchants can customize
const SSC_CONFIG = {
  // CSS selector for the container where the configurator should be mounted
  containerSelector: '#ssc-configurator',
  
  // Alternative: Auto-replace ACO options container
  acoContainerSelector: '.aco-options, .product-options, [data-aco], .product__info-container .product-form__input',
  
  // Whether to hide the original ACO options
  hideACO: true,
  
  // Show the visual preview
  showPreview: true,
  
  // Show price summary in configurator
  showPriceSummary: false,

  // ACO Field name mappings - customize these to match your ACO field names
  fieldMappings: {
    assembly: 'properties[Assembled or Unassembled?]',
    frameType: 'properties[Frame Style]',
    color: 'properties[Frame Color]',
    mesh: 'properties[Mesh Type]',
    width: 'properties[Width]',
    height: 'properties[Height]',
  }
};

// Allow global configuration override
if (window.SSC_CONFIG) {
  Object.assign(SSC_CONFIG, window.SSC_CONFIG);
}

/**
 * Initialize the configurator widget
 */
function initConfigurator() {
  // Find the target container
  let container = document.querySelector(SSC_CONFIG.containerSelector);
  
  // If no explicit container, try to find ACO options and replace them
  if (!container) {
    const acoContainer = document.querySelector(SSC_CONFIG.acoContainerSelector);
    if (acoContainer) {
      // Create a new container before ACO options
      container = document.createElement('div');
      container.id = 'ssc-configurator';
      acoContainer.parentNode.insertBefore(container, acoContainer);
      
      // Optionally hide ACO options
      if (SSC_CONFIG.hideACO) {
        acoContainer.style.display = 'none';
      }
    }
  }
  
  // If still no container, create one in the product form
  if (!container) {
    const productForm = document.querySelector('form[action*="/cart/add"]') ||
                        document.querySelector('.product-form') ||
                        document.querySelector('[data-product-form]');
    
    if (productForm) {
      container = document.createElement('div');
      container.id = 'ssc-configurator';
      
      // Find a good insertion point - before the add to cart button
      const addButton = productForm.querySelector('[type="submit"]') ||
                        productForm.querySelector('.add-to-cart') ||
                        productForm.querySelector('button');
      
      if (addButton) {
        addButton.parentNode.insertBefore(container, addButton);
      } else {
        productForm.insertBefore(container, productForm.firstChild);
      }
    }
  }
  
  if (!container) {
    console.error('[ShadeScreen Configurator] Could not find a container to mount. Add <div id="ssc-configurator"></div> to your product template.');
    return;
  }
  
  // Create React root and render
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <ShopifyConfigurator 
        showPreview={SSC_CONFIG.showPreview}
        showPriceSummary={SSC_CONFIG.showPriceSummary}
        onConfigChange={(config) => {
          // Dispatch event for external integrations
          window.dispatchEvent(new CustomEvent('ssc:configChange', {
            detail: config,
            bubbles: true
          }));
        }}
      />
    </React.StrictMode>
  );
  
  console.log('[ShadeScreen Configurator] Initialized successfully');
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initConfigurator);
} else {
  initConfigurator();
}

// Export for manual initialization
window.SSC = {
  init: initConfigurator,
  config: SSC_CONFIG
};
